import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Pencil, Trash2, Eye, FileText, ListChecks } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [formData, setFormData] = useState({
    project_name: '',
    customer_id: '',
    project_type: 'Supply Only',
    site_address: '',
    start_date: '',
    expected_end_date: '',
    status: 'Active',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, customersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/customers`, { withCredentials: true })
      ]);
      setProjects(projectsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await axios.put(`${BACKEND_URL}/api/projects/${editingProject.project_id}`, formData, {
          withCredentials: true
        });
        toast.success('Project updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/projects`, formData, {
          withCredentials: true
        });
        toast.success('Project created successfully');
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/${projectId}`, {
        withCredentials: true
      });
      toast.success('Project deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const openEditDialog = (project) => {
    setEditingProject(project);
    setFormData({
      project_name: project.project_name,
      customer_id: project.customer_id,
      project_type: project.project_type || 'Supply Only',
      site_address: project.site_address || '',
      start_date: project.start_date || '',
      expected_end_date: project.expected_end_date || '',
      status: project.status,
      notes: project.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProject(null);
    setFormData({
      project_name: '',
      customer_id: '',
      project_type: 'Supply Only',
      site_address: '',
      start_date: '',
      expected_end_date: '',
      status: 'Active',
      notes: ''
    });
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    return customer?.name || 'Unknown';
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Active': 'bg-lime-100 text-lime-800 border-lime-200',
      'Completed': 'bg-stone-100 text-stone-800 border-stone-200',
      'On Hold': 'bg-amber-100 text-amber-800 border-amber-200'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${styles[status] || styles['Active']}`}>
        {status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const styles = {
      'Supply Only': 'bg-blue-100 text-blue-800 border-blue-200',
      'Turnkey Contract': 'bg-violet-100 text-violet-800 border-violet-200',
      'Hybrid': 'bg-amber-100 text-amber-800 border-amber-200'
    };
    return (
      <span data-testid={`project-type-badge-${type}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${styles[type] || styles['Supply Only']}`}>
        {type}
      </span>
    );
  };

  const filteredProjects = projects.filter(p => typeFilter === 'ALL' || (p.project_type || 'Supply Only') === typeFilter);

  if (loading) {
    return <div className="text-stone-600">Loading projects...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Projects
          </h1>
          <p className="mt-2 text-base text-stone-600">Manage your interior projects</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-project-btn" className="bg-amber-700 hover:bg-amber-800 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Project
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProject ? 'Edit Project' : 'Add New Project'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="project_name">Project Name *</Label>
                <Input
                  id="project_name"
                  data-testid="project-name-input"
                  value={formData.project_name}
                  onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                  required
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <div>
                <Label htmlFor="customer_id">Customer *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                  required
                >
                  <SelectTrigger data-testid="project-customer-select" className="border-stone-200 focus:ring-amber-700">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.customer_id} value={customer.customer_id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="site_address">Site Address</Label>
                <Input
                  id="site_address"
                  data-testid="project-site-input"
                  value={formData.site_address}
                  onChange={(e) => setFormData({ ...formData, site_address: e.target.value })}
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="border-stone-200 focus:ring-amber-700"
                  />
                </div>
                <div>
                  <Label htmlFor="expected_end_date">Expected End Date</Label>
                  <Input
                    id="expected_end_date"
                    type="date"
                    value={formData.expected_end_date}
                    onChange={(e) => setFormData({ ...formData, expected_end_date: e.target.value })}
                    className="border-stone-200 focus:ring-amber-700"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="project_type">Project Type</Label>
                <Select
                  value={formData.project_type}
                  onValueChange={(value) => setFormData({ ...formData, project_type: value })}
                >
                  <SelectTrigger data-testid="project-type-select" className="border-stone-200 focus:ring-amber-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Supply Only">Supply Only — materials sale</SelectItem>
                    <SelectItem value="Turnkey Contract">Turnkey Contract — services/labor</SelectItem>
                    <SelectItem value="Hybrid">Hybrid — contract + add-on materials</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="border-stone-200 focus:ring-amber-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="On Hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <Button type="submit" data-testid="submit-project-btn" className="w-full bg-amber-700 hover:bg-amber-800 text-white">
                {editingProject ? 'Update Project' : 'Create Project'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="project-type-filter" className="h-8 text-xs border-stone-200 w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All project types</SelectItem>
            <SelectItem value="Supply Only">Supply Only</SelectItem>
            <SelectItem value="Turnkey Contract">Turnkey Contract</SelectItem>
            <SelectItem value="Hybrid">Hybrid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Project Name</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Type</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Customer</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Site Address</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Status</th>
                <th className="text-right px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {filteredProjects.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-stone-500">
                    {projects.length === 0 ? 'No projects found. Create your first project to get started.' : 'No projects match this filter.'}
                  </td>
                </tr>
              ) : (
                filteredProjects.map((project) => (
                  <tr key={project.project_id} data-testid={`project-row-${project.project_id}`} className="hover:bg-stone-50 transition-colors duration-200">
                    <td className="px-6 py-4 font-medium text-stone-900">{project.project_name}</td>
                    <td className="px-6 py-4">{getTypeBadge(project.project_type || 'Supply Only')}</td>
                    <td className="px-6 py-4 text-stone-700">{getCustomerName(project.customer_id)}</td>
                    <td className="px-6 py-4 text-stone-700">{project.site_address || '-'}</td>
                    <td className="px-6 py-4">{getStatusBadge(project.status)}</td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        data-testid={`view-tasks-${project.project_id}`}
                        onClick={() => navigate(`/admin/projects/${project.project_id}/tasks`)}
                        className="inline-flex items-center gap-1 text-amber-800 hover:text-amber-900 transition-colors duration-200"
                        title="Task checklist"
                      >
                        <ListChecks className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`view-orders-${project.project_id}`}
                        onClick={() => navigate(`/orders?project_id=${project.project_id}`)}
                        className="inline-flex items-center gap-1 text-lime-700 hover:text-lime-800 transition-colors duration-200"
                        title="View orders"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`view-statement-${project.project_id}`}
                        onClick={() => window.open(`/projects/${project.project_id}/statement`, '_blank')}
                        className="inline-flex items-center gap-1 text-stone-700 hover:text-stone-900 transition-colors duration-200"
                        title="View / print statement"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`edit-project-${project.project_id}`}
                        onClick={() => openEditDialog(project)}
                        className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 transition-colors duration-200"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-project-${project.project_id}`}
                        onClick={() => handleDelete(project.project_id)}
                        className="inline-flex items-center gap-1 text-red-700 hover:text-red-800 transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Projects;